import { useState, useEffect, useCallback } from "react";
export type Orientation = "landscape" | "portrait";
export interface Screen<T> {
  size: keyof T;
  isMobile: boolean;
  isMinorMobile: boolean,
  isMinorTablet: boolean;
  orientation: Orientation;
  screenIsAtLeast(breakpoint: string, andOrientation?: Orientation): boolean;
  screenIsAtMost(breakpoint: string, andOrientation?: Orientation): boolean;
}

const breakpointsName: any = {
  mobileSm: 360,
  mobile: 576,
  tablet: 768,
  laptop: 992,
  desktop: 1200,
  desktopLg: 1400,
};

const useResponsive = <T>() => {
  const sizes: any = Object.entries(breakpointsName).sort(
    ([a, aSize]: any, [b, bSize]: any) => bSize - aSize
  );
  if (sizes[sizes.length - 1][1] !== 0) {
    sizes[sizes.length - 1][1] = 0;
  }

  const getScreen = useCallback((): Screen<T> => {
    const width = window.innerWidth;
    const size = sizes?.find(([_, size]: any) => size <= width)[0];
    const orientation = width > window.innerHeight ? "landscape" : "portrait";

    return {
      size,
      isMinorTablet: width < breakpointsName.tablet,
      isMobile: width < breakpointsName.laptop,
      isMinorMobile: width < breakpointsName.mobile,
      orientation,
      screenIsAtLeast(breakpoint, andOrientation) {
        return (
          width >= breakpointsName[breakpoint] &&
          (!andOrientation || andOrientation === orientation)
        );
      },
      screenIsAtMost(breakpoint, andOrientation) {
        return (
          width <= breakpointsName[breakpoint] &&
          (!andOrientation || andOrientation === orientation)
        );
      },
    };
  }, [sizes]);

  const [screen, setScreen] = useState(getScreen());

  useEffect(() => {
    const onResize = () => {
      const current = getScreen();
      if (
        current.size !== screen.size ||
        current.orientation !== screen.orientation
      ) {
        setScreen(current);
      }
    };

    window.addEventListener("resize", onResize);

    return () => {
      window.removeEventListener("resize", onResize);
    };
  }, [screen, setScreen, getScreen]);

  return screen;
};

export default useResponsive;